import React from 'react';
import { Link } from 'react-router-dom';
import {
  ShieldCheck,
  Mail,
  Instagram,
  Heart,
  Info,
  Lock,
  Phone,
  Scale,
} from 'lucide-react';
import SEO from '../components/SEO';
import PrintButton from '../components/PrintButton';

function Block({ icon: Icon, eyebrow, title, children, testid }) {
  return (
    <section
      data-testid={testid}
      className="tf-card p-6 sm:p-7"
      style={{ background: '#fff' }}
    >
      <div className="flex items-start gap-4">
        <div
          className="w-11 h-11 rounded-2xl flex items-center justify-center flex-shrink-0"
          style={{
            background: 'var(--tf-bg-secondary)',
            color: 'var(--tf-purple)',
            border: '1px solid var(--tf-purple-light)',
          }}
        >
          <Icon size={18} />
        </div>
        <div className="flex-1 min-w-0">
          <p
            className="font-body font-bold"
            style={{
              fontSize: 10.5,
              letterSpacing: '0.22em',
              color: 'var(--tf-purple)',
            }}
          >
            {eyebrow}
          </p>
          <h2
            className="font-heading mt-1"
            style={{
              fontSize: 'clamp(20px, 2.4vw, 23px)',
              color: 'var(--tf-text)',
              letterSpacing: '-0.01em',
              lineHeight: 1.25,
            }}
          >
            {title}
          </h2>
          <div
            className="font-body mt-3 space-y-3"
            style={{
              fontSize: 13.5,
              color: 'var(--tf-text-secondary)',
              lineHeight: 1.7,
            }}
          >
            {children}
          </div>
        </div>
      </div>
    </section>
  );
}

export default function About() {
  return (
    <div className="max-w-3xl mx-auto px-5 sm:px-8 py-10" data-testid="about-screen">
      <SEO
        title="About, Privacy & Contact — The Full Impact"
        description="The Full Impact is a UK-based, non-commercial, trauma-informed survivor guide. Created by Angela Moreton. Anonymous, free, no tracking. Contact and privacy details."
        path="/app/about"
      />

      <p className="eyebrow">ABOUT · PRIVACY · CONTACT</p>
      <h1
        className="font-heading mt-3"
        style={{
          fontSize: 'clamp(28px, 4vw, 36px)',
          lineHeight: 1.15,
          color: 'var(--tf-text)',
          letterSpacing: '-0.01em',
        }}
      >
        A calm, anonymous UK survivor guide.
      </h1>
      <p
        className="font-body mt-4 max-w-xl"
        style={{ fontSize: 14.5, color: 'var(--tf-text-secondary)', lineHeight: 1.65 }}
      >
        The Full Impact is a not-for-profit, non-commercial, trauma-informed
        information service for adult survivors of sexual violence and domestic abuse in
        England &amp; Wales. It exists so anyone can read what they need, anonymously,
        without an account, sign-up, or anything stored about them.
      </p>

      <div className="mt-6 flex gap-2 flex-wrap">
        <PrintButton label="Print / Save this page" />
      </div>

      <div className="mt-8 flex flex-col gap-4">
        <Block icon={Info} eyebrow="ABOUT" title="What this site is — and isn&apos;t" testid="about-mission">
          <p>
            <strong>What it is:</strong> a free, plain-English UK guide explaining the
            criminal justice process for sexual offences, the Domestic Abuse Act 2021,
            coercive control law, safety planning, trauma responses, and where to find
            specialist support. Every helpline listed is an established UK charity or
            statutory service.
          </p>
          <p>
            <strong>What it isn&apos;t:</strong> a legal service, a counselling service,
            or a reporting platform. Nothing on this site is legal advice. For legal
            advice please speak to a solicitor or an ISVA/IDVA. For counselling, please
            contact one of the specialist services listed on the{' '}
            <Link to="/app/support" className="tf-link">Support</Link> page.
          </p>
          <p>
            <strong>Who built it:</strong> Original writing, design and selection of
            resources by Angela Moreton, a survivor and trauma-informed researcher
            based in the United Kingdom. Material is quoted with attribution from Rape
            Crisis England &amp; Wales, the Crown Prosecution Service, the Victims&apos;
            Code of Practice, gov.uk, NHS, Mind and PTSD UK.
          </p>
          <p>
            <Link to="/app/why" data-testid="about-why-link" className="tf-link" style={{ fontWeight: 700 }}>
              Read Angela&apos;s note — why she created The Full Impact &rarr;
            </Link>
          </p>
          <p>
            Content is licensed under{' '}
            <a
              href="https://creativecommons.org/licenses/by-nc-nd/4.0/"
              target="_blank"
              rel="noopener noreferrer"
              className="tf-link"
              data-testid="about-cc-link"
            >
              Creative Commons BY-NC-ND 4.0
            </a>{' '}
            — you may share it freely with attribution, but not commercially and not in
            modified form.
          </p>
        </Block>

        <Block icon={Lock} eyebrow="PRIVACY" title="What we store about you — almost nothing" testid="about-privacy">
          <p>
            <strong>No accounts.</strong> There is no sign-up. We never ask for your name,
            email, phone number, address, or any identifying detail.
          </p>
          <p>
            <strong>No personal tracking.</strong> We do not store your IP address, your
            user-agent, or any cookies that could identify you. There are no advertising
            trackers. There are no third-party social pixels.
          </p>
          <p>
            <strong>What is counted (anonymously).</strong> So we can prove this guide is
            useful when asking for funding or policy change, we count anonymous page
            views — &ldquo;the rights page was viewed N times this week.&rdquo; That total
            is not linked to any person and cannot be traced back to you. Aggregate
            referral source (e.g. &ldquo;came from Google&rdquo;) is recorded; specific
            URLs are not.
          </p>
          <p>
            <strong>Anything you write in the Journal or Guided Journal</strong> stays in
            your browser&apos;s local storage. It is <em>never</em> uploaded to any
            server. If you close the tab in private/incognito mode it is gone forever.
            You can clear it any time from your browser settings.
          </p>
          <p>
            <strong>Quick Exit.</strong> Tapping &ldquo;Exit Now&rdquo; (bottom-left of every
            page) immediately closes the tab or redirects to a neutral site, so the page
            you were reading does not stay in your tab history.
          </p>
          <p>
            <strong>Hosting &amp; cookies.</strong> The site is hosted on standard web
            infrastructure inside the European Economic Area. No advertising or
            cross-site tracking cookies are set. A small first-party analytics cookie may
            be used purely to deduplicate anonymous page-view counts and contains no
            personal data.
          </p>
        </Block>

        <Block icon={ShieldCheck} eyebrow="SAFEGUARDING" title="If you are in danger" testid="about-safeguarding">
          <p>
            If you or someone else is in immediate danger, call{' '}
            <a href="tel:999" className="tf-link">999</a>. If you cannot speak, dial 999
            then press <strong>55</strong> — the Silent Solution will alert the operator
            that you need help. For 24/7 specialist support call{' '}
            <a href="tel:08088029999" className="tf-link">0808 802 9999</a>{' '}
            (Rape Crisis) or{' '}
            <a href="tel:08082000247" className="tf-link">0808 2000 247</a>{' '}
            (National Domestic Abuse Helpline · Refuge).
          </p>
          <p>
            This site is not a crisis service. It does not record, escalate, or report
            anything you read or write. It cannot reply to messages. If you need someone
            to talk to right now, please contact one of the specialist organisations on
            the <Link to="/app/support" className="tf-link">Support</Link> page.
          </p>
        </Block>

        <Block icon={Scale} eyebrow="ACCESSIBILITY" title="Designed to be read by anyone, anywhere" testid="about-accessibility">
          <p>
            Every page works on mobile, tablet and desktop. There is a built-in
            accessibility panel (bottom-right) for larger text, higher contrast, a
            dyslexia-friendly font and reduced motion. Pages can be printed or saved as
            PDF for offline reading. Every helpline is a tap-to-call link on phones.
          </p>
          <p>
            If anything on the site does not work for you, please get in touch (below)
            and we will fix it.
          </p>
        </Block>

        <Block icon={Mail} eyebrow="CONTACT" title="Get in touch" testid="about-contact">
          <p>
            For permission requests (translations, training, research citations), to
            report a broken helpline number, to suggest a service to add, or to send
            corrections — please reach out via Instagram:
          </p>
          <a
            href="https://www.instagram.com/thefullimpactcampaign?igsh=cHEyd2Jjbm9jMjhx"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="about-instagram"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full font-bold mt-2"
            style={{
              background: '#fff',
              color: 'var(--tf-purple)',
              border: '1px solid var(--tf-purple-light)',
              fontSize: 13,
              textDecoration: 'none',
            }}
          >
            <Instagram size={15} />
            @thefullimpactcampaign
          </a>
          <p>
            We aim to reply within a few working days. Please do <strong>not</strong>{' '}
            send case details or anything we&apos;d need to act on urgently — Instagram
            is not a safeguarding channel.
          </p>
        </Block>

        <Block icon={Phone} eyebrow="FOR WEB FILTER ADMINISTRATORS" title="Site classification" testid="about-classification">
          <p>
            If you are an IT or compliance team and this site has been blocked by your
            web filter, please note: <strong>thefullimpact.org</strong> is a
            non-commercial, trauma-informed information service for adult survivors of
            sexual violence and domestic abuse. It is{' '}
            <strong>not adult content</strong>. It contains no images of violence, no
            graphic descriptions, and no user-generated content.
          </p>
          <p>
            Suggested category: <em>Charitable / Health &amp; Wellbeing / Reference</em>.
            We are happy to assist with categorisation reviews — please contact us via
            Instagram above.
          </p>
        </Block>

        <Block icon={Heart} eyebrow="COPYRIGHT" title="Use, share, attribute" testid="about-copyright">
          <p>
            © {new Date().getFullYear()} Angela Moreton. The Full Impact™ —
            personal use only. All rights reserved on original writing, design and
            structure. Quoted material from Rape Crisis, CPS, NHS, gov.uk and other UK
            sources remains the copyright of its respective owners.
          </p>
        </Block>
      </div>
    </div>
  );
}
